import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarDays, Compass, FolderOpen, Gamepad2, Heart, Home, LogOut, Menu, Music, PartyPopper,
  Search, Settings, Shield, Trophy, Users, X, Clapperboard,
} from 'lucide-react';
import SearchOverlay from './SearchOverlay';
import RefreshButton from '../../RefreshButton';

/** Onglets de la barre mobile — cinq entrées, pas une de plus. */
const TABS = [
  { to: '/', label: 'Accueil', icon: Home, exact: true },
  { to: '/explorer', label: 'Explorer', icon: Compass },
  { to: '/acteurs', label: 'Acteurs', icon: Users },
  { to: '/explorer?view=list', label: 'Ma liste', icon: Heart, match: '/explorer?view=list' },
];

/** Navigation principale du bandeau desktop. */
const NAV = [
  { to: '/', label: 'Accueil', exact: true },
  { to: '/explorer?type=film', label: 'Films' },
  { to: '/explorer?type=serie', label: 'Séries' },
  { to: '/acteurs', label: 'Acteurs' },
  { to: '/explorer?view=list', label: 'Ma liste' },
];

/**
 * Enveloppe applicative : bandeau supérieur, barre d'onglets mobile,
 * tiroir et recherche plein écran.
 *
 * Extrait des 900 lignes de JSX qui vivaient dans `App` : la navigation est
 * désormais un composant autonome, testable et réutilisable par toutes les
 * pages, au lieu d'être recopiée entre desktop et mobile.
 */
export default function AppShell({
  currentUser,
  watchlistCount = 0,
  onOpenSettings,
  onRequestMovie,
  onOpenTheater,
  onOpenPlaylists,
  onRefresh,
  onOpenAdmin,
  onLogout,
  children,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [stuck, setStuck] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const here = `${location.pathname}${location.search}`;

  /* Bandeau : opaque dès qu'on quitte le haut de page. */
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Le tiroir se referme dès qu'on navigue. */
  useEffect(() => {
    setMenuOpen(false);
  }, [here]);

  /* Raccourci clavier universel pour la recherche. */
  useEffect(() => {
    const onKey = (event) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(event.target.tagName) || event.target.isContentEditable;
      if ((event.key === 'k' && (event.metaKey || event.ctrlKey)) || (event.key === '/' && !typing)) {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /**
   * Un onglet est actif si son chemin correspond **et** que tous ses
   * paramètres sont présents à l'identique. Un onglet sans paramètre
   * (« Explorer ») ne s'allume que sur une exploration neutre, sinon il
   * s'allumerait en même temps que « Ma liste » ou « Films ».
   */
  const isActive = (item) => {
    if (item.exact) return location.pathname === '/';

    const [path, query = ''] = (item.match || item.to).split('?');
    if (location.pathname !== path) return false;

    const wanted = new URLSearchParams(query);
    const current = new URLSearchParams(location.search);

    if (![...wanted].every(([key, value]) => current.get(key) === value)) return false;
    if (![...wanted].length) return !current.get('view') && !current.get('type');
    return true;
  };

  const go = (to) => {
    navigate(to);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const extras = [
    { icon: Gamepad2, label: 'Jeux', hint: 'Bibliothèque de jeux', action: () => go('/games') },
    { icon: Trophy, label: 'Succès', hint: 'Vos trophées', action: () => go('/achievements') },
    { icon: CalendarDays, label: 'Calendrier', hint: 'Sorties des séries', action: () => go('/calendrier') },
    { icon: Music, label: 'Musique', hint: 'La salle d’écoute', action: () => go('/musique') },
    { icon: PartyPopper, label: 'Watch Party', hint: 'Regarder ensemble', action: onOpenTheater },
    { icon: FolderOpen, label: 'Collections', hint: 'Vos playlists', action: onOpenPlaylists },
    { icon: Clapperboard, label: 'Demander un film', hint: 'Suggérer un ajout', action: onRequestMovie },
  ];

  return (
    <>
      <a className="skip-link" href="#contenu">
        Aller au contenu
      </a>

      <header className={`hdr${stuck ? ' is-stuck' : ''}`}>
        <div className="hdr-inner">
          <button type="button" className="hdr-logo" onClick={() => go('/')} aria-label="Accueil">
            {/* Variante monochrome : le logo rose d'origine jurait avec
                l'identité or. Un léger virage sépia le rend laiton. */}
            <img src="/assets/logoFull720x180-nb.svg" alt="Pop Movie" />
          </button>

          <nav className="hdr-nav" aria-label="Navigation principale">
            {NAV.map((item) => (
              <button
                key={item.to}
                type="button"
                className={`hdr-tab${isActive(item) ? ' is-on' : ''}`}
                aria-current={isActive(item) ? 'page' : undefined}
                onClick={() => go(item.to)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hdr-actions">
            <button
              type="button"
              className="icon-btn tip"
              data-tip="Rechercher (Ctrl K)"
              onClick={() => setSearchOpen(true)}
              aria-label="Rechercher"
            >
              <Search aria-hidden="true" />
            </button>

            <span className="hidden lg:contents">
              <RefreshButton onRefresh={onRefresh} />
              <button
                type="button"
                className="icon-btn tip"
                data-tip="Paramètres"
                onClick={onOpenSettings}
                aria-label="Paramètres"
              >
                <Settings aria-hidden="true" />
              </button>
              {currentUser?.role === 'admin' && (
                <button
                  type="button"
                  className="icon-btn tip"
                  data-tip="Administration"
                  onClick={onOpenAdmin}
                  aria-label="Administration"
                >
                  <Shield aria-hidden="true" />
                </button>
              )}
            </span>

            <button
              type="button"
              className="icon-btn"
              onClick={() => setMenuOpen(true)}
              aria-label="Ouvrir le menu"
              aria-expanded={menuOpen}
            >
              <Menu aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div id="contenu">{children}</div>

      {/* ─────────────── Barre d'onglets mobile ─────────────── */}
      <nav className="tabbar" aria-label="Navigation">
        {TABS.map((item) => {
          const Icon = item.icon;
          const on = isActive(item);
          return (
            <button
              key={item.to}
              type="button"
              className={`tabbar-item${on ? ' is-on' : ''}`}
              aria-current={on ? 'page' : undefined}
              onClick={() => go(item.to)}
            >
              <Icon aria-hidden="true" />
              <span>{item.label}</span>
              {item.label === 'Ma liste' && watchlistCount > 0 && (
                <span className="tabbar-badge">{watchlistCount > 99 ? '99+' : watchlistCount}</span>
              )}
            </button>
          );
        })}
        <button type="button" className="tabbar-item" onClick={() => setMenuOpen(true)}>
          <Menu aria-hidden="true" />
          <span>Plus</span>
        </button>
      </nav>

      {/* ─────────────── Tiroir ─────────────── */}
      {menuOpen && (
        <>
          <div className="scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />
          <aside className="drawer" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="drawer-head">
              <div className="min-w-0">
                <p className="eyebrow eyebrow-gold">Connecté</p>
                <p className="text-[var(--t-hi)] font-semibold truncate">
                  {currentUser?.username || 'Invité'}
                </p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setMenuOpen(false)}
                aria-label="Fermer le menu"
              >
                <X aria-hidden="true" />
              </button>
            </div>

            <div className="drawer-body">
              {NAV.map((item) => (
                <button
                  key={item.to}
                  type="button"
                  className={`drawer-row${isActive(item) ? ' is-on' : ''}`}
                  onClick={() => go(item.to)}
                >
                  {item.label}
                  {item.label === 'Ma liste' && watchlistCount > 0 && (
                    <span className="meta">{watchlistCount}</span>
                  )}
                </button>
              ))}

              <div className="drawer-sep" />
              <p className="eyebrow drawer-label">Explorer autrement</p>

              {extras.map(({ icon: Icon, label, hint, action }) => (
                <button
                  key={label}
                  type="button"
                  className="drawer-row"
                  onClick={() => {
                    setMenuOpen(false);
                    action && action();
                  }}
                >
                  <Icon aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block">{label}</span>
                    <span className="block text-[var(--fs-xs)] text-[var(--t-faint)]">{hint}</span>
                  </span>
                </button>
              ))}

              <div className="drawer-sep" />

              <button
                type="button"
                className="drawer-row"
                onClick={() => {
                  setMenuOpen(false);
                  onOpenSettings && onOpenSettings();
                }}
              >
                <Settings aria-hidden="true" />
                Paramètres
              </button>

              {currentUser?.role === 'admin' && (
                <button
                  type="button"
                  className="drawer-row"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenAdmin && onOpenAdmin();
                  }}
                >
                  <Shield aria-hidden="true" />
                  Administration
                </button>
              )}

              <button
                type="button"
                className="drawer-row"
                style={{ color: 'var(--c-curtain-hot)' }}
                onClick={onLogout}
              >
                <LogOut aria-hidden="true" />
                Se déconnecter
              </button>
            </div>
          </aside>
        </>
      )}

      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
    </>
  );
}
